import React, { useEffect, useRef, useState } from 'react';
import { Hero } from './components/Hero';
import { Controls } from './components/Controls';
import { ResultsGrid } from './components/ResultsGrid';
import { useScanState } from './hooks/useScanState';
import { UniverseKey, NIFTY50, NIFTY_NEXT_50, NIFTY100, NIFTY200, NIFTY500, SENSEX30 } from './constants/stockUniverses';
import api from './services/api';
import './styles/App.css';

const TOKEN_KEY = 'upstox_holdings_token';

function App() {
  const { isLoading, error, results, runScan, fetchLatestResults, resetState } = useScanState();
  const [query, setQuery] = useState('');
  const [showNoSignal, setShowNoSignal] = useState(false);
  const [universe, setUniverse] = useState<UniverseKey>('NIFTY50');
  const [holdingsSymbols, setHoldingsSymbols] = useState<Set<string>>(new Set());
  const [holdingsLoading, setHoldingsLoading] = useState(false);
  const [holdingsError, setHoldingsError] = useState<string | null>(null);
  const [showTokenPrompt, setShowTokenPrompt] = useState(false);
  const [tokenInput, setTokenInput] = useState('');
  const tokenRef = useRef<HTMLInputElement>(null);

  const [user, setUser] = useState<{ email: string; name: string } | null>(null);
  const [authChecked, setAuthChecked] = useState(false);

  useEffect(() => {
    api.getMe().then(me => {
      setUser(me);
      setAuthChecked(true);
    });
  }, []);

  useEffect(() => { if (user) fetchLatestResults(); }, [user]);

  useEffect(() => {
    if (showTokenPrompt) setTimeout(() => tokenRef.current?.focus(), 50);
  }, [showTokenPrompt]);

  const loadHoldings = async (token?: string) => {
    setHoldingsLoading(true);
    setHoldingsError(null);
    try {
      const symbols = await api.getHoldings(token);
      setHoldingsSymbols(new Set(symbols));
      setShowTokenPrompt(false);
    } catch (err: any) {
      const status = err?.response?.status;
      if (!err?.response) {
        setHoldingsError(`Cannot reach the backend server at ${api.getBaseUrl()}.`);
        setUniverse('ALL');
      } else if (status === 401 || status === 403) {
        // Token expired or wrong — prompt user to enter their valid token
        localStorage.removeItem(TOKEN_KEY);
        setShowTokenPrompt(true);
        setHoldingsError(null);
      } else if (status === 400) {
        setShowTokenPrompt(true);
        setHoldingsError(null);
      } else {
        setHoldingsError(`Holdings error (${status ?? 'unknown'}): ${err.response?.data?.error ?? err.message}`);
        setUniverse('ALL');
      }
    } finally {
      setHoldingsLoading(false);
    }
  };

  const handleUniverseChange = async (u: UniverseKey) => {
    setUniverse(u);
    setHoldingsError(null);
    setShowTokenPrompt(false);
    if (u === 'HOLDINGS') {
      const stored = localStorage.getItem(TOKEN_KEY) ?? undefined;
      await loadHoldings(stored);
    }
  };

  const handleTokenSave = async () => {
    const token = tokenInput.trim();
    if (!token) return;
    localStorage.setItem(TOKEN_KEY, token);
    setTokenInput('');
    await loadHoldings(token);
  };

  const matchesUniverse = (symbol: string): boolean => {
    switch (universe) {
      case 'NIFTY50':     return NIFTY50.has(symbol);
      case 'NIFTYNEXT50': return NIFTY_NEXT_50.has(symbol);
      case 'NIFTY100':    return NIFTY100.has(symbol);
      case 'NIFTY200':    return NIFTY200.has(symbol);
      case 'NIFTY500':    return NIFTY500.has(symbol);
      case 'SENSEX30':    return SENSEX30.has(symbol);
      case 'HOLDINGS':    return holdingsSymbols.has(symbol);
      default:            return true; // ALL
    }
  };

  // Results within the selected universe (drives the signal tiles; ignores the search box)
  const byUniverse = results.filter(r => matchesUniverse(r.symbol));

  // Same set, further narrowed by the search box (drives the results grid)
  const filtered = query.trim()
    ? byUniverse.filter(r => r.symbol.toLowerCase().includes(query.trim().toLowerCase()))
    : byUniverse;

  // Universe stays the full scanned seed (500); signal counts react to the selected chip
  const tileStats = {
    totalProcessed: NIFTY500.size,
    inCriteria:    byUniverse.filter(r => r.classification === 'BUY').length,
    gettingBetter: byUniverse.filter(r => r.classification === 'HOLD').length,
    fallingAway:   byUniverse.filter(r => r.classification === 'SELL').length,
  };

  const displayError = error || holdingsError;
  const displayLoading = isLoading || holdingsLoading;

  if (!authChecked) {
    return <div className="app-shell" />;
  }

  if (!user) {
    return (
      <div className="app-shell login-shell">
        <div className="card login-card">
          <h1>Stock Pranitk</h1>
          <p>Sign in with an authorized Google account to continue.</p>
          <button className="btn btn-primary" onClick={() => api.login()}>
            Sign in with Google
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="app-shell">
      <div className="user-bar">
        <span>{user.email}</span>
        <button className="btn btn-secondary" onClick={() => api.logout()}>Sign out</button>
      </div>
      <Hero stats={tileStats} />
      <Controls
        isLoading={displayLoading}
        error={displayError}
        onRunScan={async () => { await runScan(); }}
        onLoadDemo={async () => { resetState(); await fetchLatestResults(); }}
        query={query}
        onQueryChange={setQuery}
        showNoSignal={showNoSignal}
        onToggleNoSignal={() => setShowNoSignal(v => !v)}
        universe={universe}
        onUniverseChange={handleUniverseChange}
      />

      {universe === 'HOLDINGS' && showTokenPrompt && !holdingsLoading && (
        <div className="card token-prompt">
          <p className="token-prompt-label">
            Enter your Upstox access token to load your holdings:
          </p>
          <div className="token-prompt-row">
            <input
              ref={tokenRef}
              type="password"
              className="token-prompt-input"
              placeholder="Paste Upstox access token..."
              value={tokenInput}
              onChange={e => setTokenInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleTokenSave(); }}
            />
            <button
              className="btn btn-primary"
              onClick={handleTokenSave}
              disabled={!tokenInput.trim()}
            >
              Load Holdings
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => { setShowTokenPrompt(false); setUniverse('ALL'); }}
            >
              Cancel
            </button>
          </div>
          <p className="token-prompt-hint">
            Token is stored in your browser only and is used for read-only access to your holdings —
            no trading or extra permission is requested. Upstox access tokens expire daily, so if you
            get a 401 just paste a fresh one from{' '}
            <span style={{ color: 'var(--accent)' }}>Upstox &rarr; Account &rarr; API &rarr; Access Token</span>.
          </p>
        </div>
      )}

      {universe === 'HOLDINGS' && holdingsSymbols.size > 0 && !holdingsLoading && (
        <div className="holdings-notice">
          {filtered.length === 0
            ? `None of your ${holdingsSymbols.size} holding(s) are in the Nifty 500 scan universe. Run a scan first.`
            : `Showing ${filtered.length} of ${holdingsSymbols.size} holding(s) that are in the scan universe.`}
        </div>
      )}

      <ResultsGrid results={filtered} isLoading={displayLoading} showNoSignal={showNoSignal} />
    </div>
  );
}

export default App;
